/**
 * Core Game Type Definitions
 *
 * Fundamental types used throughout the Super Reversi game.
 * These types define the core game state, player information,
 * and basic game mechanics.
 */

// Basic game constants
export const BOARD_SIZE = 8;
export const TOTAL_SQUARES = 64;
export const DIRECTIONS = [-9, -8, -7, -1, 1, 7, 8, 9] as const;

// Player and piece representation
export enum PlayerColor {
  EMPTY = 0,
  BLACK = 1,
  WHITE = 2,
}

export enum PlayerType {
  HUMAN = 'human',
  AI = 'ai',
}

export enum GameStatus {
  WAITING = 'waiting',
  IN_PROGRESS = 'in_progress',
  PAUSED = 'paused',
  GAME_OVER = 'game_over',
}

// Move quality grades
export enum Grade {
  C = 'C',    // Basic moves (0-9 points)
  B = 'B',    // Good moves (10-17 points)
  A = 'A',    // Great moves (18-29 points)
  S = 'S',    // Exceptional moves (30-44 points)
  SS = 'SS',  // Master moves (45-59 points)
  SSS = 'SSS' // Legendary moves (60+ points)
}

// AI difficulty levels
export enum AIDifficulty {
  RANDOM = 'v0',      // Random valid moves
  GREEDY = 'v1',      // Immediate best score
  MINIMAX = 'v2',     // Look-ahead with alpha-beta pruning
  ADVANCED = 'v3',    // Opening book + minimax + endgame solver
  MINIMAX_EASY = 'v2a',    // Minimax depth 2-4
  MINIMAX_HARD = 'v2b',    // Minimax depth 4-6 + features
  MASTER = 'v3_master',    // Maximum strength advanced AI
  BLITZ = 'v3_blitz',     // Fast advanced AI for quick games
  CUSTOM = 'custom',       // User-configurable AI
}

// Board position (0-63)
export type BoardPosition = number;

// Position value constants for scoring
export const POSITION_VALUES = {
  CORNER: 25,
  EDGE: 6,
  X_SQUARE: -10,  // Diagonal from corner
  C_SQUARE: -6,   // Adjacent to corner
  NORMAL: 0,
} as const;

// Grade thresholds
export const GRADE_THRESHOLDS = {
  B: 10,
  A: 18,
  S: 30,
  SS: 45,
  SSS: 60,
} as const;

// Animation and timing constants
export const ANIMATION_TIMINGS = {
  PREPARATION: 200,  // ms - 0.2 second prep phase
  FLIP: 400,         // ms - 0.4 second flip animation
  TOTAL: 600,        // ms - Total animation duration
  EFFECTS_MIN: 350,  // ms - Minimum effect duration
  EFFECTS_MAX: 900,  // ms - Maximum effect duration
} as const;

// Performance limits
export const PARTICLE_LIMITS = {
  MIN: 1200,
  MAX: 1800,
  LOW_PERFORMANCE: 600,
} as const;

export const PERFORMANCE_TARGETS = {
  TARGET_FPS: 60,
  MAX_FRAME_TIME: 16, // ms for 60fps
  AI_RESPONSE_TIME: {
    v0: 100,        // ms - Random should be instant
    v1: 1000,       // ms - Greedy evaluation
    v2: 3000,       // ms - Minimax search
    v3: 5000,       // ms - Advanced with endgame
    v2a: 2000,      // ms - Easy minimax
    v2b: 4000,      // ms - Hard minimax
    v3_master: 10000, // ms - Master level
    v3_blitz: 1500,   // ms - Blitz games
    custom: 5000,     // ms - Default for custom
  },
} as const;

// Player information
export interface Player {
  type: PlayerType;
  color: PlayerColor;
  name: string;
  stats: PlayerStats;
}

export interface PlayerStats {
  movesPlayed: number;
  averageScore: number;
  gradeDistribution: Record<Grade, number>;
  totalThinkingTime: number;
  gamesWon: number;
  gamesLost: number;
  gamesTied: number;
}

// Game configuration
export interface GameOptions {
  playerBlack: PlayerType;
  playerWhite: PlayerType;
  aiDifficulty?: AIDifficulty;
  startingPlayer?: PlayerColor;
  boardSize?: number; // Always 8 for standard Reversi
}

// Board utilities
export interface BoardPosition2D {
  row: number;
  col: number;
}

export interface Point {
  x: number;
  y: number;
}

// Validation helpers
export const isValidPosition = (position: BoardPosition): boolean => {
  return position >= 0 && position < TOTAL_SQUARES;
};

export const isValidColor = (color: PlayerColor): boolean => {
  return Object.values(PlayerColor).includes(color);
};

export const positionToCoords = (position: BoardPosition): BoardPosition2D => {
  return {
    row: Math.floor(position / BOARD_SIZE),
    col: position % BOARD_SIZE,
  };
};

export const coordsToPosition = (row: number, col: number): BoardPosition => {
  return row * BOARD_SIZE + col;
};

export const positionToPoint = (position: BoardPosition, cellSize: number = 50): Point => {
  const coords = positionToCoords(position);
  return {
    x: coords.col * cellSize + cellSize / 2,
    y: coords.row * cellSize + cellSize / 2,
  };
};

// Position analysis helpers
export const isCorner = (position: BoardPosition): boolean => {
  return [0, 7, 56, 63].includes(position);
};

export const isEdge = (position: BoardPosition): boolean => {
  const coords = positionToCoords(position);
  return coords.row === 0 || coords.row === 7 || coords.col === 0 || coords.col === 7;
};

export const isXSquare = (position: BoardPosition): boolean => {
  // Squares diagonally adjacent to corners
  return [9, 14, 49, 54].includes(position);
};

export const isCSquare = (position: BoardPosition): boolean => {
  // Squares adjacent to corners (excluding X-squares)
  return [1, 6, 8, 15, 48, 55, 57, 62].includes(position);
};

export const getPositionValue = (position: BoardPosition): number => {
  if (isCorner(position)) {return POSITION_VALUES.CORNER;}
  if (isXSquare(position)) {return POSITION_VALUES.X_SQUARE;}
  if (isCSquare(position)) {return POSITION_VALUES.C_SQUARE;}
  if (isEdge(position)) {return POSITION_VALUES.EDGE;}
  return POSITION_VALUES.NORMAL;
};

// Direction helpers for move validation
export const getDirectionOffset = (direction: number): BoardPosition2D => {
  return {
    row: Math.floor(direction / BOARD_SIZE) - 1,
    col: (direction % BOARD_SIZE) - 1,
  };
};

export const isValidDirection = (from: BoardPosition, direction: number): boolean => {
  const fromCoords = positionToCoords(from);
  const dirOffset = getDirectionOffset(direction + 8); // Adjust for array indexing
  const newRow = fromCoords.row + dirOffset.row;
  const newCol = fromCoords.col + dirOffset.col;

  return newRow >= 0 && newRow < BOARD_SIZE && newCol >= 0 && newCol < BOARD_SIZE;
};

// Type guards
export const isPlayerColor = (value: unknown): value is PlayerColor => {
  return typeof value === 'number' && Object.values(PlayerColor).includes(value as PlayerColor);
};

export const isGrade = (value: unknown): value is Grade => {
  return typeof value === 'string' && Object.values(Grade).includes(value as Grade);
};

export const isAIDifficulty = (value: unknown): value is AIDifficulty => {
  return typeof value === 'string' && Object.values(AIDifficulty).includes(value as AIDifficulty);
};

export const isGameStatus = (value: unknown): value is GameStatus => {
  return typeof value === 'string' && Object.values(GameStatus).includes(value as GameStatus);
};

// Error types for game operations
export class InvalidMoveError extends Error {
  constructor(
    message: string,
    public readonly position: BoardPosition,
    public readonly reason: string,
  ) {
    super(message);
    this.name = 'InvalidMoveError';
  }
}

export class GameStateError extends Error {
  constructor(
    message: string,
    public readonly gameStatus: GameStatus,
  ) {
    super(message);
    this.name = 'GameStateError';
  }
}

export class AIError extends Error {
  constructor(
    message: string,
    public readonly difficulty: AIDifficulty,
    public readonly cause?: Error,
  ) {
    super(message);
    this.name = 'AIError';
  }
}